/* ============================================================
   EOL ANIMATED HERO — Edge of Life Designs
   assets/eol-animated-hero.js
   ============================================================ */

(function () {
  'use strict';

  class EOLHeroPanel {
    constructor(panelEl, options) {
      this.panel = panelEl;
      this.options = options;
      this.slides = Array.from(panelEl.querySelectorAll('.eol-panel__slide'));
      this.currentIndex = 0;
      this.timer = null;
      this.isTransitioning = false;

      if (this.slides.length <= 1) return;
      if (options.autoplay) this.startAutoplay();
    }

    startAutoplay() {
      this.timer = setInterval(() => {
        if (!this.isTransitioning) this.nextSlide();
      }, this.options.slideDuration);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    nextSlide() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.goToSlide(nextIndex);
    }

    goToSlide(nextIndex) {
      if (nextIndex === this.currentIndex || this.isTransitioning) return;
      this.isTransitioning = true;

      const current = this.slides[this.currentIndex];
      const next = this.slides[nextIndex];
      const style = this.options.transitionStyle;
      const duration = this.options.transitionDuration;

      // Lazy load next image if needed
      const lazyImg = next.querySelector('img[data-src]');
      if (lazyImg) {
        lazyImg.src = lazyImg.dataset.src;
        if (lazyImg.dataset.srcset) lazyImg.srcset = lazyImg.dataset.srcset;
        lazyImg.removeAttribute('data-src');
        lazyImg.removeAttribute('data-srcset');
      }

      if (style === 'none') {
        current.classList.remove('eol-panel__slide--active');
        next.classList.add('eol-panel__slide--active');
        this.currentIndex = nextIndex;
        this.isTransitioning = false;
        return;
      }

      // Set up transition
      next.style.opacity = '0';
      next.classList.add('eol-panel__slide--active');
      current.classList.add('eol-panel__slide--outgoing');
      current.classList.remove('eol-panel__slide--active');

      if (style === 'fade-black') {
        // Fade out current, then fade in next
        current.style.transition = `opacity ${duration / 2}ms ease`;
        current.style.opacity = '0';
        setTimeout(() => {
          next.style.transition = `opacity ${duration / 2}ms ease`;
          next.style.opacity = '1';
        }, duration / 2);
      } else if (style === 'soft-dissolve') {
        current.style.transition = `opacity ${duration}ms ease, filter ${duration}ms ease`;
        current.style.opacity = '0';
        current.style.filter = 'blur(4px)';
        next.style.transition = `opacity ${duration}ms ease, filter ${duration}ms ease`;
        next.style.filter = 'blur(4px)';
        setTimeout(() => {
          next.style.opacity = '1';
          next.style.filter = 'blur(0px)';
        }, 50);
      } else if (style === 'slide') {
        current.style.transition = `transform ${duration}ms ease`;
        current.style.transform = 'translateX(-100%)';
        next.style.transform = 'translateX(100%)';
        next.style.opacity = '1';
        setTimeout(() => {
          next.style.transition = `transform ${duration}ms ease`;
          next.style.transform = 'translateX(0)';
        }, 50);
      } else {
        // Crossfade (default)
        current.style.transition = `opacity ${duration}ms ease`;
        current.style.opacity = '0';
        next.style.transition = `opacity ${duration}ms ease`;
        setTimeout(() => {
          next.style.opacity = '1';
        }, 50);
      }

      setTimeout(() => {
        current.classList.remove('eol-panel__slide--outgoing');
        current.style.transition = '';
        current.style.opacity = '';
        current.style.transform = '';
        current.style.filter = '';
        next.style.transition = '';
        next.style.transform = '';
        next.style.filter = '';
        this.currentIndex = nextIndex;
        this.isTransitioning = false;
      }, duration + 100);
    }

    destroy() {
      this.stopAutoplay();
    }
  }

  class EOLAnimatedHero {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.panelA = sectionEl.querySelector('.eol-panel-a');
      this.panelB = sectionEl.querySelector('.eol-panel-b');
      this.syncSlides = sectionEl.dataset.syncSlides === 'true';
      this.syncAnims = sectionEl.dataset.syncAnims === 'true';

      this.heroA = null;
      this.heroB = null;

      this.init();
    }

    init() {
      this.initPanel('a');
      this.initPanel('b');
      this.initTextAnimations();
      this.initSyncedSlides();
    }

    initPanel(id) {
      const panelEl = id === 'a' ? this.panelA : this.panelB;
      if (!panelEl) return;

      const options = {
        autoplay: panelEl.dataset.autoplay === 'true',
        slideDuration: parseInt(panelEl.dataset.slideDuration, 10) || 8000,
        transitionDuration: parseInt(panelEl.dataset.transitionDuration, 10) || 1200,
        transitionStyle: panelEl.dataset.transitionStyle || 'crossfade',
      };

      const hero = new EOLHeroPanel(panelEl, options);
      if (id === 'a') this.heroA = hero;
      else this.heroB = hero;
    }

    initSyncedSlides() {
      if (!this.syncSlides || !this.heroA || !this.heroB) return;

      // Stop individual timers and use a shared one
      this.heroA.stopAutoplay();
      this.heroB.stopAutoplay();

      const duration = Math.min(
        this.heroA.options.slideDuration,
        this.heroB.options.slideDuration
      );

      setInterval(() => {
        if (!this.heroA.isTransitioning) this.heroA.nextSlide();
        if (!this.heroB.isTransitioning) this.heroB.nextSlide();
      }, duration);
    }

    initTextAnimations() {
      const animEls = this.section.querySelectorAll('.eol-text-anim');
      if (!animEls.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      animEls.forEach(el => observer.observe(el));
    }

    destroy() {
      if (this.heroA) this.heroA.destroy();
      if (this.heroB) this.heroB.destroy();
    }
  }

  // Init all hero sections on page
  function initAllHeroes() {
    document.querySelectorAll('.eol-hero').forEach(el => {
      if (!el._eolHero) {
        el._eolHero = new EOLAnimatedHero(el);
      }
    });
  }

  // Shopify theme editor live reload support
  document.addEventListener('DOMContentLoaded', initAllHeroes);

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target.querySelector('.eol-hero');
      if (section && !section._eolHero) {
        section._eolHero = new EOLAnimatedHero(section);
      }
    });
    document.addEventListener('shopify:section:unload', (e) => {
      const section = e.target.querySelector('.eol-hero');
      if (section && section._eolHero) {
        section._eolHero.destroy();
        delete section._eolHero;
      }
    });
  }
})();
